// TODO: other channel properties mentioned in project.js:
// level, frozen, hidden, thumbnailUrl, migratedToS3, sharedWith, libraryName, libraryDescription,
// latestLibraryVersion, publishLibrary, libraryPublishedAt
//
// Do we still want/need these? Should they be on a separate type?
// If the ChannelsApi on the server doesn't care about these, they should
// live elsewhere.
// The library data should definitely live elsewhere.

import {Theme} from '@code-dot-org/component-library/common/contexts';
import {ExcalidrawElement} from '@excalidraw/excalidraw/types/element/types';
import {
  ExcalidrawInitialDataState,
  BinaryFileData,
  DataURL,
} from '@excalidraw/excalidraw/types/types';
import type {EdgeMarkerType} from '@xyflow/system';
import type * as BlocklyCore from 'blockly/core';
import {ComponentType, CSSProperties, LazyExoticComponent} from 'react';

import {BlockDefinition} from '@cdo/apps/blockly/types';
import {LevelPredictSettings} from '@cdo/apps/lab2/levelEditors/types';
import {AiTutorPromptSettings} from '@cdo/apps/weblab2/types';

import {lab2EntryPoints} from '../../lab2EntryPoints';
import type {
  ImageNodeData,
  LineAnchorNodeData,
  ShapeNodeData,
  TextNodeData,
} from '../sketchlab/reactFlow/types';

export {Theme};

/// ------ USER APP OPTIONS ------ ///

// Partial definition of the UserAppOptions structure, only defining the
// pieces we need at the moment.
export interface PartialUserAppOptions {
  isInstructor: boolean;
}

/// ------ PROJECTS ------ ///

/** Identifies a project. Corresponds to the "value" JSON column for the entry in the projects table. */
export interface Channel {
  id: string;
  name: string;
  isOwner: boolean;
  projectType: ProjectType;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  hidden?: boolean;
  thumbnailUrl?: string;
  frozen?: boolean;
  // Certain project types (like bubble choice standalone projects) can have subprojects.
  subprojects?: {level_id: number; channel_id: string}[];
  // Optional lab-specific configuration for this project.  If provided, this will be saved
  // to the Project model in the database along with the other entries in this interface,
  // inside the value field JSON.
  labConfig?: {[key: string]: {[key: string]: string}};
}

export type DefaultChannel = Pick<Channel, 'name'>;

/** A project and its corresponding sources if present, fetched together when loading a level. */
export interface ProjectAndSources {
  // When projects are loaded for the first time, sources may not be present
  sources?: ProjectSources;
  channel: Channel;
  abuseScore?: number;
  sharingDisabled?: boolean;
  isTeacherOfProjectOwner?: boolean;
}

/// ------ SOURCES ------ ///

// Represents the structure of the full project sources object (i.e. the main.json file)
export interface ProjectSources {
  // Source code can either be a string, Blockly JSON, or a nested JSON object (for multi-file).
  source: string | Source;
  // Optional lab-specific configuration for this project
  labConfig?: LabConfig;
  // Add other properties (animations, html, etc) as needed.
}

export type LabConfig = {[key: string]: {[key: string]: string}};

export type Source =
  | BlocklySource
  | MultiFileSource
  | ExcalidrawSourceWithExternalFiles
  | SketchlabReactFlowSource;

// -- REACT FLOW SKETCH LAB -- //

// Serializable node/edge types for project storage. These mirror the
// @xyflow/react Node/Edge fields we persist, without the complex DOM
// types that are incompatible with Immer's WritableDraft. Cast to/from
// the full React Flow types at the read/write boundary.
interface SketchlabReactFlowNodeBase {
  id: string;
  position: {x: number; y: number};
  style?: CSSProperties;
}

export type SketchlabReactFlowNode =
  | (SketchlabReactFlowNodeBase & {type: 'shape'; data: ShapeNodeData})
  | (SketchlabReactFlowNodeBase & {type: 'text'; data: TextNodeData})
  | (SketchlabReactFlowNodeBase & {type: 'image'; data: ImageNodeData})
  | (SketchlabReactFlowNodeBase & {
      type: 'lineAnchor';
      data: LineAnchorNodeData;
    });

export interface SketchlabReactFlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  type?: string;
  markerEnd?: EdgeMarkerType;
}

export interface SketchlabReactFlowSource {
  nodes: SketchlabReactFlowNode[];
  edges: SketchlabReactFlowEdge[];
  viewport?: {x: number; y: number; zoom: number};
}

export interface SaveSourceOptions {
  projectType?: string;
}

export interface UpdateSourceOptions extends SaveSourceOptions {
  currentVersion: string;
  replace: boolean;
  firstSaveTimestamp: string;
  tabId: string | null;
}

// -- BLOCKLY -- //

// Blockly JSON is currently typed as a generic object
export type BlocklySource = {[key: string]: unknown};

// -- SKETCH LAB -- //

export type SketchlabExternalFiles = Record<FileId, SketchlabProjectFile>;

// By default, Excalidraw file entries require a dataURL field that has a
// base64 encoding of the file. As we move to store images in S3, this field
// is now optional.
type ExcalidrawFileWithOptionalData = Omit<BinaryFileData, 'dataURL'> & {
  dataURL?: DataURL;
};

export type ExcalidrawFilesWithOptionalData = Record<
  ExcalidrawElement['id'],
  ExcalidrawFileWithOptionalData
>;

// We add the externalFiles property to Excalidraw's default state
// to map each file to an external URL (a location in S3) where we store the image.
// We override the files property with a version of their file type where the dataURL
// is not required (ie, since we're storing the image in S3 instead of as a base64 encoded string).
export type ExcalidrawSourceWithExternalFiles = Omit<
  ExcalidrawInitialDataState,
  'files'
> & {
  files?: ExcalidrawFilesWithOptionalData;
  externalFiles?: SketchlabExternalFiles;
};

export type SketchlabProjectFile = Pick<ProjectFile, 'id' | 'url'> & {
  uploaded?: boolean;
  starterAsset?: boolean;
  filenameWithExtension?: string;
};

// -- MULTI-FILE -- //

export type FileId = string;
export type FolderId = string;

// This structure (as well as ProjectFolder and ProjectFile) is still in flux
// and may change going forward. It should only be used for labs that are not released
// yet.
// Note that if it changes files_api.has_valid_encoding? may need to be updated to correctly validate
// the new structure.
export interface MultiFileSource {
  folders: Record<FolderId, ProjectFolder>;
  files: Record<FileId, ProjectFile>;
  openFiles?: FileId[];
}

export interface ProjectFile {
  id: FileId;
  name: string;
  contents: string;
  active?: boolean;
  folderId: string;
  type?: ProjectFileType;
  url?: string;
  flagged?: boolean;
  isAiTutorVersionUpdated?: boolean;
  isAiTutorVersionCreated?: boolean;
}

/**
 * Project file types are as follows:
 * Starter: Files that come from level start code that are editable by the user.
 * Support: Files that come from level start code that are hidden and not editable by the user.
 * Validation: The file that contain the level's validation code, which is a code file that will be
 * run by the lab. This file is hidden from users.
 * Locked Starter: Files that come from level start code that are editable by the user, but cannot be
 *  deleted or renamed.
 * System Support: Files that are used for running code and for share/remix, but are hidden from the user.
 *  For example, the serialized maze for a neighborhood level.
 *
 *  NOTE: we have some logic that assumes that if a file has been assigned one of these types,
 *  that it was uploaded by a levelbuilder. If that changes, we should update the logic that decides whether to
 *  delete a file from S3 in multiFileSourceEditUtils.
 */
export enum ProjectFileType {
  STARTER = 'starter',
  SUPPORT = 'support',
  VALIDATION = 'validation',
  LOCKED_STARTER = 'locked_starter',
  SYSTEM_SUPPORT = 'system_support',
}

export interface ProjectFolder {
  id: FolderId;
  name: string;
  parentId: string;
  open?: boolean;
}

/// ------ LEVELS ------ ///

/**
 * Labs may extend this type to add lab-specific properties.
 */
export interface LevelProperties {
  // Not a complete list; add properties as needed.
  id: number;
  name: string;
  isProjectLevel?: boolean;
  hideShareAndRemix?: boolean;
  usesProjects?: boolean;
  levelData?: LevelData;
  appName: AppName;
  longInstructions?: string;
  freePlay?: boolean;
  edit_blocks?: string;
  isK1?: boolean;
  skin?: string;
  // Dance stores the full main.json source structure (ProjectSources) in start/template/exemplar sources,
  // while PythonLab/Weblab2 stores just the source code (MultiFileSource). TODO: Can we reconcile these?
  startSources?: ProjectSources | MultiFileSource;
  templateSources?: ProjectSources | MultiFileSource;
  validations?: Validation[];
  baseAssetUrl?: string;
  // An optional URL that allows the user to skip the progression.
  skipUrl?: string;
  // Project Template level name for the level if it exists.
  projectTemplateLevelName?: string;
  // Help and Tips values
  mapReference?: string;
  referenceLinks?: string[];
  helpVideos?: VideoData[];
  // Exemplars
  showExemplarLink?: boolean;
  exemplarSources?: ProjectSources | MultiFileSource;
  exemplarSettings?: ExemplarSettings;
  // For Teachers Only value
  teacherMarkdown?: string;
  predictSettings?: LevelPredictSettings;
  productTours?: string[];
  submittable?: boolean;
  disableEditRunForSubmission?: boolean;
  finishUrl?: string;
  finishDialog?: ShareDialogId;
  offerBrowserTts?: boolean;
  useSecondaryFinishButton?: boolean;
  // Python Lab/Codebridge specific properties
  validationFile?: ProjectFile;
  enableMicroBit?: boolean;
  miniApp?: string;
  serializedMaze?: MazeCell[][];
  startDirection?: number;
  widgetView?: boolean;
  widgetViewAllowShowCode?: boolean;
  aiTutorMode?: string;
  aiTutorPromptSettings?: AiTutorPromptSettings;
  levelSystemPrompt?: string;
  // Properties added for parity with non-lab2 AI Tutor levels
  aiTutorAvailable?: boolean;
  isAssessment?: boolean;
  type?: string;
  starterAssets?: {[key: string]: string};
  showRubric?: boolean;
  customHelperLibrary?: string;
  validationCode?: string;
  hideVersionHistory?: boolean;
  parentLevelName?: string;
  requireEditToContinue?: boolean;
}

export type LevelPropertiesMap = {[levelId: string]: LevelProperties};

export interface BlocklyLevelProperties extends LevelProperties {
  toolboxDefinition?: BlocklyCore.utils.toolbox.ToolboxInfo;
  sharedBlocks?: BlockDefinition[];
}

// Level configuration data used by project-backed labs that don't require
// reloads between levels. Labs may define more specific fields.
export interface ProjectLevelData {
  startSources: Source;
}

// The level data for a standalone_video level that doesn't require
// reloads between levels.
export interface VideoLevelData {
  src: string;
  download: string;
  thumbnail: string;
}

// The level data for a bubble_choice level that doesn't require
// reloads between levels.
export interface BubbleChoiceLevelData {
  displayName: string;
  description: string;
  sublevels: BubbleChoiceSublevel[];
  hideLetters: boolean;
}

// Bubble Choice specific property
export interface BubbleChoiceSublevel {
  display_name: string;
  description?: string;
  level_id: string;
  thumbnail_url: string;
  url: string;
  position: number;
}

// Addtional fields for videos that are linked as references in the
// Help & Tips tab of Instructions.
interface VideoData extends VideoLevelData {
  name?: string;
  key?: string;
  enable_fallback?: boolean;
  autoplay?: boolean;
}

// Exemplar settings for a level.
export interface ExemplarSettings {
  validationEnabled: boolean;
  validationSuccessMessage: string;
  validationFailureMessage: string;
}

// Python Lab specific property
export interface MazeCell {
  tileType: number;
  value: number;
  assetId: number;
}

// Configuration for how a Lab should be rendered
export interface Lab2EntryPoint {
  /**
   * A lazy loaded view for the lab. This should be a lazy-loaded react
   * component using a dynamic import. See `pythonlab/entrypoint.tsx` for an
   * example.
   */
  view: LazyExoticComponent<ComponentType<LabProps>>;
  /**
   * An array of themes that the lab supports.
   */
  themes: Theme[];
}

export type LevelData =
  | ProjectLevelData
  | VideoLevelData
  | BubbleChoiceLevelData;

export type ProjectType =
  | AppName
  | StandaloneAppName
  | 'artist'
  | 'artist_k1'
  | 'frozen'
  | 'minecraft_adventurer'
  | 'minecraft_hero'
  | 'minecraft_designer'
  | 'minecraft_codebuilder'
  | 'minecraft_aquatic'
  | 'algebra_game'
  | 'starwars'
  | 'starwarsblocks_hour'
  | 'iceage'
  | 'infinity'
  | 'gumball'
  | 'playlab'
  | 'playlab_k1'
  | 'sports'
  | 'basketball'
  | 'music_dance_ai';

export type AppName = keyof typeof lab2EntryPoints;

export type StandaloneAppName =
  | 'spritelab'
  | 'story'
  | 'science'
  | 'poetry_hoc'
  | 'poetry'
  | 'time_capsule'
  | 'dance';

/// ------ VALIDATIONS ------ ///

// A validation condition.
export interface Condition {
  name: string;
  value?: string | number;
}

type ValueType = 'string' | 'number';
type ConditionValueType = `${ValueType}:${ValueType}` | ValueType;
export interface ConditionType {
  name: string;
  valueType?: ConditionValueType;
  description: string;
  valueOptions?: string[];
}

// Validation in the level.
export interface Validation {
  conditions: Condition[];
  message: string;
  callout?: string;
  next: boolean;
  key: string;
  comment?: string;
}

/// ------ MISC ------ ///

export interface ExtraLinksLevelData {
  links: {[key: string]: {text: string; url: string; access_key?: string}[]};
  can_clone: boolean;
  can_delete: boolean;
  level_name: string;
  script_level_path_links: ScriptLevelPathLink[];
  parent_level_path_links: ParentLevelPathLink[];
  is_standalone_project: boolean;
}
export interface ExtraLinksProjectData {
  owner_info?: {storage_id: number; name: string};
  project_info?: {
    id: number;
    sources_link: string;
    is_featured_project: boolean;
    featured_status: string;
    remix_ancestry: string[];
    is_published_project: 'yes' | 'no';
    abuse_score: number;
  };
  meesage?: string;
}

export interface ProjectVersion {
  versionId: string;
  lastModified: string;
  isLatest: boolean;
  comment?: string;
}

export interface ScriptLevelPathLink {
  script: string;
  path: string;
}

export interface ParentLevelPathLink {
  level_name: string;
  path: string;
  kind: string;
  position: string;
}

export interface LabProps<
  T extends LevelProperties = LevelProperties,
  U extends ProjectSources = ProjectSources
> {
  levelProperties: T;
  initialSources?: U;
  channel?: Channel;
}

export type ShareDialogId = 'hoc2024' | 'hoai2025';

export type LevelNavigationConfirmation = () => boolean | Promise<boolean>;
